import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// One-time guard so the auto-recovery below can never loop: set right before we
// reload, cleared once we're genuinely authenticated. Scoped to sessionStorage
// so it lives exactly as long as the tab's login attempt.
const RECOVERY_KEY = 'auth_callback_recovery_attempted';

// AuthCallback is rendered at /auth/callback. react-oidc-context handles the
// token exchange automatically (the AuthProvider sees the `code` and `state`
// params on mount). Once the user is authenticated we navigate to the root,
// which then routes through RequireAuth → Landing.
export function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (auth.isAuthenticated) {
      sessionStorage.removeItem(RECOVERY_KEY);
      navigate('/', { replace: true });
    }
  }, [auth.isAuthenticated, navigate]);

  // Auto-recover from a spurious callback error. The common failure here is a
  // double-consumed authorization code: the first exchange succeeds and a user
  // is persisted to sessionStorage, but a second processing of the same code
  // (StrictMode double-invoke, a silent-SSO race, or orphaned OIDC state left
  // by an abandoned signup — login and signup share one state namespace) then
  // raises `invalid_grant` / "No matching state" into auth.error, while the
  // in-memory context never flips to authenticated. A manual page refresh fixes
  // it because the AuthProvider re-reads the already-persisted user from
  // storage. We automate that single refresh here rather than dead-ending on an
  // error screen. Reloading to `/` (not back to the callback) means: if a user
  // was persisted, RequireAuth lets them straight in; if not, RequireAuth
  // starts a clean interactive login. The RECOVERY_KEY guard bounds this to one
  // attempt, so a genuine, persistent error still surfaces the terminal UI.
  useEffect(() => {
    if (!auth.error) return;
    if (sessionStorage.getItem(RECOVERY_KEY)) return;
    sessionStorage.setItem(RECOVERY_KEY, '1');
    window.location.replace(`${window.location.origin}/`);
  }, [auth.error]);

  if (auth.error && sessionStorage.getItem(RECOVERY_KEY)) {
    return (
      <main className="page">
        <p role="alert" className="error">
          {t('auth.error')}
        </p>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            sessionStorage.removeItem(RECOVERY_KEY);
            void auth.signinRedirect();
          }}
        >
          {t('auth.retry')}
        </button>
      </main>
    );
  }

  return (
    <main className="page">
      <p>{t('auth.completing')}</p>
    </main>
  );
}
